import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { FormSection, InlineError, ToggleRow, showToast } from "../components/index.ts";

/**
 * "Carry unused sessions to the next package" (#ai-feedback 2026-10-26, card #1), on the Packages
 * page. Off (the default): at the end of a package, its unused sessions end with it. On: they move
 * to the package that waits for it (the expiry job, `carryOverAtExpiry`). With no package that
 * waits, they end with the package. Carry over on one package (the client page) works in both cases.
 */
export function CarryOverRuleCard({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  const member = label("member");
  const on = business.data?.settings.carryOverSessions === true;
  return (
    <FormSection
      title="Unused sessions"
      description={`When a package ends, the sessions a ${member} did not use end with it. You can move them to the next package of the ${member} instead: the package that starts after this one.`}
    >
      <ToggleRow
        label="Carry unused sessions to the next package"
        hint={
          on
            ? "On: at the end of a package, its unused sessions go to the next package. With no next package, they end."
            : "Off: unused sessions end with the package. You can still use Carry over on one package."
        }
        checked={on}
        disabled={update.isPending || !business.data}
        onChange={(next) =>
          update.mutate(
            { settings: { carryOverSessions: next } },
            { onSuccess: () => showToast(next ? "Unused sessions go to the next package from now on." : "Unused sessions end with the package from now on.") },
          )
        }
      />
      {update.error ? <InlineError error={update.error} /> : null}
    </FormSection>
  );
}
